<template>
  <div :class="clsx('contacts-and-subscribe-wrapper', { 'animations-disabled': !animationEnabled })" id="contacts">
    <IntersectionObserver
      id="subscribe"
      class="contacts-and-subscribe-intersector"
      :animation-enabled="animationEnabled"
      :once="true"
      once-class="contacts-and-subscribe-intersector--visible"
      :threshold="0.3"
    >
      <div class="contacts-and-subscribe__block">
        <div class="contacts-and-subscribe__contacts">
          <h2 class="contacts-and-subscribe__contacts__title" v-html="getTitleBlock('promo', 'contacts', 'title')"></h2>
          <ul class="contacts-and-subscribe__contacts__list">
            <li class="contacts-and-subscribe__contacts__item contacts-and-subscribe__contacts__item--email">
              <EmailImage class="contacts-and-subscribe__contacts__icon" />
              <span v-html="getKeyValueListBlockValue('promo', 'contacts-list', 'email')"></span>
            </li>
            <li class="contacts-and-subscribe__contacts__item contacts-and-subscribe__contacts__item--telephone">
              <TelephoneImage class="contacts-and-subscribe__contacts__icon" />
              <span v-html="getKeyValueListBlockValue('promo', 'contacts-list', 'telephone')"></span>
            </li>
            <li class="contacts-and-subscribe__contacts__item contacts-and-subscribe__contacts__item--country">
              <RoundPushpinImage class="contacts-and-subscribe__contacts__icon" />
              <span v-html="getKeyValueListBlockValue('promo', 'contacts-list', 'location')"></span>
            </li>
            <li class="contacts-and-subscribe__contacts__item contacts-and-subscribe__contacts__item--schedule">
              <ClockImage class="contacts-and-subscribe__contacts__icon" />
              <span v-html="getKeyValueListBlockValue('promo', 'contacts-list', 'working-hours')"></span>
            </li>
          </ul>
        </div>
        <form class="contacts-and-subscribe__subscribe-form" @submit.prevent="handleSubmit">
          <div>
            <LabeledTextInput
              label="Імʼя"
              placeholder="Введіть ваше ім'я"
              class="contacts-and-subscribe__subscribe-form__input--name"
              v-model="form.name"
              :error-messages="errors.name ? [errors.name] : []"
              @input="clearError('name')"
            />
          </div>
          <PhoneInput
            class="contacts-and-subscribe__subscribe-form__input--phone"
            v-model="form.phone"
            :error-message="errors.phone"
            @input="clearError('phone')"
          />
          <div>
            <LabeledTextInput
              label="Email"
              placeholder="example@email.com"
              class="contacts-and-subscribe__subscribe-form__input--email"
              v-model="form.email"
              :error-messages="errors.email ? [errors.email] : []"
              @input="clearError('email')"
            />
          </div>
          <OrangeFancyButton
            type="submit"
            class="contacts-and-subscribe__subscribe-form__subscribe-button"
          >
            Записатись на презентацію
          </OrangeFancyButton>
        </form>
      </div>
      <div class="contacts-and-subscribe__background-blocks">
        <BackgroundGradientBlue class="contacts-and-subscribe__background-gradient--blue" />
      </div>
      <div class="contacts-and-subscribe__footer"></div>
    </IntersectionObserver>
    <SubscribeInfoPopup
      ref="subscribeInfoPopup"
      :title="getTitleBlock('promo', 'subscribe-notification', 'title')"
      :message="getTitleBlock('promo', 'subscribe-notification', 'description')"
    />
  </div>
</template>

<script>
import clsx from 'clsx';
import { mapActions } from 'vuex';
import BackgroundGradientBlue from './background-gradient-blue';
import SubscribeInfoPopup from './subscribe-info-popup';
import { textBlock } from '@/entities/text-block';
import { MetaTrackerEvents } from '@/shared/config/tracker-events';
import { metaTracker } from '@/shared/lib/tracker';
import { LabeledTextInput, OrangeFancyButton, PhoneInput } from '@/shared/ui/form';
import { IntersectionObserver } from '@/shared/ui/intersection-observer';
import { ClockImage, EmailImage, RoundPushpinImage, TelephoneImage } from '@/shared/ui/promo';

export default {
  props: {
    animationEnabled: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [ textBlock, metaTracker ],
  components: {
    ClockImage, EmailImage, RoundPushpinImage, OrangeFancyButton, TelephoneImage, LabeledTextInput, PhoneInput,
    IntersectionObserver, BackgroundGradientBlue, SubscribeInfoPopup
  },
  data() {
    return {
      form: {
        name: '',
        phone: '',
        email: '',
      },
      errors: {
        name: '',
        phone: '',
        email: '',
      },
    };
  },
  methods: {
    ...mapActions('promoPresentationRequestsModule', ['createPresentationRequest']),
    clsx,
    validateName(name) {
      if (!name || name.trim().length === 0) {
        return 'Ім\'я є обов\'язковим';
      }
      if (name.trim().length < 2) {
        return 'Ім\'я повинно містити мінімум 2 символи';
      }
      return '';
    },
    validatePhone(phone) {
      if (!phone || phone.trim().length === 0) {
        return 'Телефон є обов\'язковим';
      }

      const digits = phone.replace(/\D/g, '');

      if (digits.length !== 12 || !digits.startsWith('38')) {
        return 'Введіть коректний номер телефону';
      }

      return '';
    },
    validateEmail(email) {
      if (!email || email.trim().length === 0) {
        return 'Email є обов\'язковим';
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return 'Введіть коректну email адресу';
      }
      return '';
    },
    validateForm() {
      this.errors.name = this.validateName(this.form.name);
      this.errors.phone = this.validatePhone(this.form.phone);
      this.errors.email = this.validateEmail(this.form.email);

      return !this.errors.name && !this.errors.phone && !this.errors.email;
    },
    clearError(field) {
      this.errors[field] = '';
    },
    showSubscribeInfoPopup() {
      this.$refs.subscribeInfoPopup.open();
    },
    hideSubscribeInfoPopup() {
      this.$refs.subscribeInfoPopup.close();
    },
    async handleSubmit() {
      if (this.validateForm()) {
        const response = await this.createPresentationRequest({ name: this.form.name, telephone: this.form.phone, email: this.form.email });

        if (response.status === 'Success!') {
          this.form.name = '';
          this.form.phone = '';
          this.form.email = '';

          this.fireMetaTrackerEvent(MetaTrackerEvents.PresentationRequest);
          this.showSubscribeInfoPopup();
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.contacts-and-subscribe-intersector {
  position: relative;
  background-color: #FCFDFF;
}

.contacts-and-subscribe__block {
  display: flex;
  justify-content: space-between;
  padding: 95px 55px;

  @media (max-width: 1200px) {
    padding: 64px 16px;
  }

  @media (max-width: 800px) {
    flex-direction: column;
  }
}

.contacts-and-subscribe__contacts {
  margin-bottom: 32px;
  position: relative;
  z-index: 1;

  @media (max-width: 800px) {
    z-index: auto;
  }

  &__list {
    position: relative;
    width: fit-content;
    z-index: 1;
  }

  &__title {
    position: relative;
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 52px;
    font-weight: 500;
    color: #313437;
    margin-bottom: 32px;
    transform: translateY(100%);
    opacity: 0;
    z-index: 1;
    animation: slideup-opacity-text-animation 0.5s linear forwards;
    animation-play-state: paused;

    @media (max-width: 1050px) {
      font-size: 36px;
    }

    @media (max-width: 800px) {
      font-size: 58px;
    }

    @media (max-width: 600px) {
      font-size: 36px;
    }
  }

  &__item {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #494E53;
    background-color: #FCFDFF;
    padding: 8px 16px;
    border-radius: 16px;
    display: flex;
    align-items: center;

    &:not(:last-child) {
      margin-bottom: 8px;
    }

    &--email {
      transform: translateX(-200%);
      animation: contact-item-animation 0.5s linear forwards;
      animation-delay: 1s;
      animation-play-state: paused;

      @media (max-width: 800px) {
        animation-delay: 0.25s;
      }
    }

    &--telephone {
      transform: translateX(-200%);
      animation: contact-item-animation 0.5s linear forwards;
      animation-delay: 1.15s;
      animation-play-state: paused;

      @media (max-width: 800px) {
        animation-delay: 0.4s;
      }
    }

    &--country {
      transform: translateX(-200%);
      animation: contact-item-animation 0.5s linear forwards;
      animation-delay: 1.3s;
      animation-play-state: paused;

      @media (max-width: 800px) {
        animation-delay: 0.55s;
      }
    }

    &--schedule {
      transform: translateX(-200%);
      animation: contact-item-animation 0.5s linear forwards;
      animation-delay: 1.45s;
      animation-play-state: paused;

      @media (max-width: 800px) {
        animation-delay: 0.7s;
      }
    }
  }

  &__icon {
    width: 32px;
    height: 32px;
    margin-right: 8px;
  }
}

.contacts-and-subscribe__subscribe-form {
  overflow: hidden;
  z-index: 1;

  @media (max-width: 1300px) {
    width: 50%;
    margin-left: 15px;
  }
  @media (max-width: 800px) {
    width: 100%;
    margin: auto;
  }

  &__subscribe-button {
    margin: 28px auto 0;
    transform: translateY(100%) scale(0.4);
    opacity: 0;
    animation: subscribe-form-subscribe-animation 0.5s linear forwards;
    animation-delay: 1s;
    animation-play-state: paused;

    @media (max-width: 800px) {
      animation-delay: 1.5s;
    }
  }

  &__input {
    &--name {
      transform: translateX(200%);
      animation: subscribe-form-field-animation 0.5s linear forwards;
      animation-delay: 0.25s;
      animation-play-state: paused;

      @media (max-width: 800px) {
        animation-delay: 1s;
      }
    }

    &--phone {
      transform: translateX(200%);
      animation: subscribe-form-field-animation 0.5s linear forwards;
      animation-delay: 0.4s;
      animation-play-state: paused;

      @media (max-width: 800px) {
        animation-delay: 1.15s;
      }
    }

    &--email {
      transform: translateX(200%);
      animation: subscribe-form-field-animation 0.5s linear forwards;
      animation-delay: 0.55s;
      animation-play-state: paused;

      @media (max-width: 800px) {
        animation-delay: 1.3s;
      }
    }
  }
}

.labeled-input {
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}

.error-message {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #FF3B30;
  margin-top: -12px;
  margin-bottom: 8px;
  padding-left: 4px;
}

::v-deep .input-text--error {
  color: #FF3B30;
  font-size: 12px;
  margin-top: -8px;
  margin-bottom: 12px;
}

::v-deep .input-text--has-error {
  border-color: #FF3B30;
}

::v-deep .input-label,
.input-label {
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #494E53;
  z-index: 1;
  margin-bottom: 8px;
}

::v-deep .input-text,
.input-text {
  position: relative !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  background-color: #EBF2FF !important;
  width: 515px !important;
  border-radius: 16px !important;
  border: none !important;
  color: #494E53 !important;
  padding: 16px 24px !important;
  margin-bottom: 16px !important;
  z-index: 1;

  @media (max-width: 1300px) {
    width: 100% !important;
  }

  &::placeholder {
    color: #8E9DB7 !important;
  }

  &.error {
    border: 2px solid #FF3B30;
  }
}

.contacts-and-subscribe__background-gradient--blue {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 745px;
  height: 745px;
  overflow: hidden;
}

.contacts-and-subscribe__footer {
  display: block;
  height: 32px;
  background-color: #313437;
  border-radius: 32px 32px 0 0 ;
}

.contacts-and-subscribe-intersector {
  &--visible {
    .contacts-and-subscribe__contacts__title,
    .contacts-and-subscribe__subscribe-form__input--name,
    .contacts-and-subscribe__subscribe-form__input--phone,
    .contacts-and-subscribe__subscribe-form__input--email,
    .contacts-and-subscribe__subscribe-form__subscribe-button,
    .contacts-and-subscribe__contacts__item--email,
    .contacts-and-subscribe__contacts__item--telephone,
    .contacts-and-subscribe__contacts__item--country,
    .contacts-and-subscribe__contacts__item--schedule {
      animation-play-state: running;
    }
  }
}

.animations-disabled,
.animations-disabled * {
  animation: none !important;
  animation-duration: 0s !important;
  animation-delay: 0s !important;
  transition: none !important;
}

.animations-disabled {
  .contacts-and-subscribe__contacts {
    &__title {
      clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
      transform: translateY(0);
      opacity: 1;
    }

    &__item--email, &__item--telephone, &__item--country, &__item--schedule {
      transform: translateX(0);
    }
  }

  .contacts-and-subscribe__subscribe-form {
    &__subscribe-button {
      transform: translateY(0) scale(1);
      opacity: 1;
    }

    &__input--name, &__input--phone, &__input--email {
      transform: translateX(0);
    }
  }
}

@keyframes slideup-opacity-text-animation {
  0% {
    opacity: 0;
    clip-path: polygon(0 0, 100% 0, 100% 0%, 0 0%);
    transform: translateY(100%);
  }
  20% {
    opacity: 0.2;
    clip-path: polygon(0 0, 100% 0, 100% 20%, 0 20%);
    transform: translateY(80%);
  }
  40% {
    opacity: 0.4;
    clip-path: polygon(0 0, 100% 0, 100% 40%, 0 40%);
    transform: translateY(60%);
  }
  60% {
    opacity: 0.6;
    clip-path: polygon(0 0, 100% 0, 100% 60%, 0 60%);
    transform: translateY(40%);
  }
  80% {
    opacity: 0.8;
    clip-path: polygon(0 0, 100% 0, 100% 80%, 0 80%);
    transform: translateY(20%);
  }
  100% {
    opacity: 1;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    transform: translateY(0);
  }
}

@keyframes subscribe-form-field-animation {
  from {
    transform: translateX(200%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes subscribe-form-subscribe-animation {
  0% {
    transform: translateY(100%) scale(0.4);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

@keyframes contact-item-animation {
  from {
    transform: translateX(-200%);
  }
  to {
    transform: translateX(0);
  }
}
</style>
